import React, { useEffect, useRef, useState, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ContactSection } from '../components/ContactSection';
import { BeachCameraViewer } from '../components/BeachCameraViewer';
import { motion } from 'framer-motion';
import { Wind, TrendingUp, MapPin, Waves, Sailboat, CheckCircle2, GraduationCap, Users, ShieldCheck, Package, Award, Coffee } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { captureUtmParams } from '../utils/utmTracking';
import { useNavigate } from 'react-router-dom';
const spotFeatures = [{
  icon: Wind,
  title: 'רוח צד (Side Shore)',
  description: 'תנאי רוח אידיאליים לגלישה בטוחה - הרוח נושבת לאורך החוף'
}, {
  icon: MapPin,
  title: 'מיקום מושלם בכנרת',
  description: 'חוף מעגן עדן - אחד החופים הטובים ביותר לספורט רוח'
}, {
  icon: Waves,
  title: 'מים שטוחים',
  description: 'מושלם ללמידה והתקדמות מהירה'
}];
const courses = [{
  title: 'קורס ווינג פויל למתחילים',
  description: 'לימוד מאפס, כולל תיאוריה ופרקטיקה על המים',
  icon: GraduationCap
}, {
  title: 'קורס מתקדמים',
  description: 'שיפור טכניקה, מהפכים ועוד',
  icon: TrendingUp
}, {
  title: 'הדרכות סאפ',
  description: 'שיעורי סאפ לכל הרמות - ממתחילים ועד מתקדמים',
  icon: Waves
}, {
  title: 'הדרכות קיאק וסרפסקי',
  description: 'לימוד חתירה מקצועית בקיאק ובסרפסקי',
  icon: Sailboat
}, {
  title: 'קבוצות סאפ',
  description: 'אימוני סאפ קבוצתיים באווירה כיפית ותומכת',
  icon: Users
}, {
  title: 'הדרכות אישיות',
  description: 'שיעור פרטי מותאם אישית לרמה שלך',
  icon: Users
}, {
  title: 'גלישת רוח (Windsurfing)',
  description: 'הדרכות לכל הרמות, ממתחילים ועד מתקדמים',
  icon: Sailboat
}];
const clubFeatures = [{
  title: 'שימוש חופשי בציוד חדיש ואיכותי',
  icon: ShieldCheck
}, {
  title: 'אחסון ציוד אישי במועדון',
  icon: Package
}, {
  title: 'מתקן לטליית ווינגים וקומפרסור',
  icon: Wind
}, {
  title: 'מדשאות נוחות להכנת ציוד',
  icon: MapPin
}, {
  title: 'מוזיקה ופינת קפה',
  icon: Coffee
}, {
  title: 'חניה קרובה למים',
  icon: CheckCircle2
}, {
  title: 'קהילת גולשים פעילה ותומכת',
  icon: Award
}];
function WindMeterWidget() {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // In RTL, scroll starts at the right. Scroll to left (0) to show the wind circle first.
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, []);
  return <div ref={scrollRef} dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto" data-id="element-2453" data-mp="386qskn44tfvj">
      <div className="absolute inset-0 z-10" data-id="element-2454" data-mp="3hwk2ljomlmxo" />
      <iframe src="https://widget.holfuy.com/?station=2020&su=knots&te=C&lang=he&bg=ffffff" className="h-[320px] border-0" style={{
      minWidth: '520px',
      width: '100%'
    }} title="Wind Station - Beach Club Maagan" scrolling="no" data-id="element-2455" data-mp="3hydbz6qmil9d" />
    </div>;
}
export function WindMeterPage() {
  const navigate = useNavigate();
  const [isCameraReady, setIsCameraReady] = useState(false);
  useEffect(() => {
    captureUtmParams();
    document.title = 'מד רוח ומצלמת כנרת';
    let metaReferrer = document.querySelector('meta[name="referrer"]');
    if (!metaReferrer) {
      metaReferrer = document.createElement('meta');
      metaReferrer.setAttribute('name', 'referrer');
      document.head.appendChild(metaReferrer);
    }
    metaReferrer.setAttribute('content', 'strict-origin-when-cross-origin');
    setIsCameraReady(true);
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'מד רוח ומצלמת כנרת בזמן אמת בחוף מעגן עדן. צפו בחוף ובדקו רוח, טמפרטורה ותנאי גלישה לפני היציאה למים');
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'מד רוח ומצלמת כנרת, מצלמת כנרת, מצלמת חוף כנרת, מד רוח כנרת, רוח כנרת, תנאי רוח כנרת, חוף מעגן עדן');
    const ogTags = [{
      property: 'og:title',
      content: 'מד רוח ומצלמת כנרת'
    }, {
      property: 'og:description',
      content: 'צפו במצלמת חוף מעגן ובדקו את תנאי הרוח בכנרת בזמן אמת לפני היציאה למים'
    }, {
      property: 'og:type',
      content: 'website'
    }, {
      property: 'og:url',
      content: window.location.href
    }];
    ogTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', tag.content);
    });
  }, []);
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-2456" data-mp="1nqt1t9iqiyrh">
      <Navigation data-id="element-2457" data-mp="1socmos9xu9se" />

      <div className="relative h-[50vh] min-h-[400px] overflow-hidden bg-gradient-to-br from-[#0A5F8F] to-[#0D7AB8]" data-id="element-2458" data-mp="1snt7hjbo9zi2">
        <div className="absolute inset-0 opacity-10" data-id="element-2459" data-mp="1wpeford9hplx">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" data-id="element-2460" data-mp="21go1thzlqat2" />
        </div>
        <div className="relative z-10 container mx-auto px-4 h-full flex flex-col items-center justify-center text-center text-white" data-id="element-2461" data-mp="26f7ojkm0rqmh">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-2462" data-mp="2bh6savlo8pjy">
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md px-6 py-3 rounded-full mb-6 border border-white/30" data-id="element-2463" data-mp="2kdo6k7pc13jx">
              <Wind className="w-6 h-6" data-id="element-2464" data-mp="q17q6e2to91c" />
              <span className="font-medium text-lg" data-id="element-2465" data-mp="q29dbgx44lzf">תנאי רוח בזמן אמת</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-id="element-2466" data-mp="1td6s3q24x5zx">
              מד רוח ומצלמת כנרת
            </h1>
            <p className="text-xl md:text-2xl font-light max-w-2xl mx-auto text-blue-100" data-id="element-2467" data-mp="1ofeo3xo7ttfd">
              צפו בחוף ובדקו את תנאי הרוח לפני שאתם יוצאים למים
            </p>
          </motion.div>
        </div>
      </div>

      <section className="py-16 bg-gray-50" data-id="element-2468" data-mp="1ye46rsnxrhwo">
        <div className="container mx-auto px-4" data-id="element-2469" data-mp="2cahewb6ky6yh">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-6xl mx-auto" data-id="element-2470" data-mp="2c96ac95b8v6b">
            <div className="text-center mb-8" data-id="element-2471" data-mp="2h2hlunl2i04k">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-4" data-id="element-2472" data-mp="2h2rao7udm18o">
                מד רוח בזמן אמת
              </h2>
              <p className="text-gray-600 text-lg" data-id="element-2473" data-mp="2uzo3ufxyvusq">
                נתוני רוח, טמפרטורה ותנאי מזג אוויר מעודכנים כל דקה
              </p>
            </div>
            <div className="max-w-2xl mx-auto mb-6" data-id="element-2474" data-mp="39q0rt2it446z">
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-start gap-3 text-right" data-id="element-2475" data-mp="3ensmu5er06fe">
                <Wind className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" data-id="element-2476" data-mp="30osw8s7odhgl" />
                <p className="text-blue-800 text-sm font-medium" data-id="element-2477" data-mp="34pmi7g1k4m6u">
                  ברוחות מערביות, בתוך המים יש כ-3-4 קשרים יותר ממה שמראה מד
                  הרוח.
                </p>
              </div>
            </div>

            <BeachCameraViewer isReady={isCameraReady} data-id="element-2478" data-mp="2vp7vcc8y2z18" />

            <WindMeterWidget data-id="element-2479" data-mp="2vqrdk8e5vk5n" />
            <div dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto mt-6" data-id="element-2480" data-mp="2h0k42i64tbh3">
              <div className="absolute inset-0 z-10" data-id="element-2481" data-mp="2lx25b8296roo" />
              <iframe src="https://widget.holfuy.com/?mode=graphs&station=2020&su=knots" className="h-[580px] md:h-[850px] border-0" style={{
              minWidth: '520px',
              width: '100%'
            }} title="Wind Graphs - Beach Club Maagan" scrolling="no" data-id="element-2482" data-mp="27z3ta9ben7cc" />
            </div>
            <div dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto mt-6" data-id="element-2483" data-mp="2nh3r68sjs3e9">
              <div className="absolute inset-0 z-10" data-id="element-2484" data-mp="2ikd95fx6mxq7" />
              <iframe src="https://api.holfuy.com/archive/?s=2020" className="h-[600px] md:h-[700px] border-0" style={{
              minWidth: '520px',
              width: '100%'
            }} title="Wind Archive - Beach Club Maagan" scrolling="no" data-id="element-2485" data-mp="2efnh625na3ak" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gray-50" data-id="element-2486" data-mp="1zsc2hre62368">
        <div className="container mx-auto px-4" data-id="element-2487" data-mp="1uszl3m62dob1">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-2488" data-mp="1uuaovlziew25">
            <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-600 px-4 py-2 rounded-full font-bold mb-4" data-id="element-2489" data-mp="1lu4be03t9w2z">
              <GraduationCap className="w-5 h-5" data-id="element-2490" data-mp="68tqhw8xc4hu" />
              <span data-id="element-2491" data-mp="67imbvbkguq7">ללמוד ולגלוש</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-2492" data-mp="aa5j8an3h5u9">
              קורסים והדרכות לכל הרמות
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-2493" data-mp="f98c6wpsvllv">
              ממתחילים שעושים צעדים ראשונים ועד גולשים מתקדמים שרוצים לשפר
              טכניקה - המים השטוחים של הכנרת הופכים את הלמידה לקלה ובטוחה.
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-2494" data-mp="3nptmqhy2ymr3">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2495" data-mp="3sga42d50usiq">
              <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-2496" data-mp="2zzpokq3nvf7h">
                ההדרכות שלנו
              </h3>
              <div className="space-y-6" data-id="element-2497" data-mp="2b0wlmlc6wsz6">
                {courses.map((course, index) => <div key={index} className="flex gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-2498" data-mp="2f1qbvld3ujv8" data-mpi={index}>
                    <div className="bg-cyan-50 p-3 rounded-xl h-fit shrink-0" data-id="element-2499" data-mp="2k1cn5p9dhq1e">
                      <course.icon className="w-6 h-6 text-cyan-600" data-id="element-2500" data-mp="2oyuwvhv7s9ag" />
                    </div>
                    <div data-id="element-2501" data-mp="1rd70uy2o7szg">
                      <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2502" data-mp="1rdqgu98rg7au">
                        {course.title}
                      </h4>
                      <p className="text-gray-600" data-id="element-2503" data-mp="21algwwkomfr8">{course.description}</p>
                    </div>
                  </div>)}
              </div>
              <a href="https://wa.me/972522632784" target="_blank" rel="noopener noreferrer" className="mt-8 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white text-lg font-bold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-3 shadow-md" data-id="element-2504" data-mp="261tpjqe6tx3c">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" data-id="element-2505" data-mp="161rnsi877fru">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2506" data-mp="112ep907ysk9f" />
                </svg>
                לתיאום שיעור
              </a>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2507" data-mp="3gwi448rp5zsw">
              <div className="bg-[#0A5F8F] rounded-3xl p-8 md:p-10 text-white h-full relative overflow-hidden" data-id="element-2508" data-mp="cfyyd5kg8876">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2509" data-mp="3uuzphzu4r07n" />
                <div className="relative z-10" data-id="element-2510" data-mp="rej8p6brqqal">
                  <h3 className="text-2xl font-bold mb-8" data-id="element-2511" data-mp="rdrb5pym2iho">מה כולל המועדון?</h3>
                  <ul className="space-y-5" data-id="element-2512" data-mp="w5hnu2l0tic9">
                    {clubFeatures.map((feature, index) => <li key={index} className="flex items-center gap-4" data-id="element-2513" data-mp="15jjhss6paqv9" data-mpi={index}>
                        <div className="bg-white/10 p-2 rounded-lg shrink-0" data-id="element-2514" data-mp="15howhcriowqn">
                          <feature.icon className="w-5 h-5 text-cyan-300" data-id="element-2515" data-mp="11i7qmi86hxky" />
                        </div>
                        <span className="text-lg text-blue-50" data-id="element-2516" data-mp="1fbyylm69h9ek">
                          {feature.title}
                        </span>
                      </li>)}
                  </ul>
                  <div className="mt-10 pt-8 border-t border-white/20" data-id="element-2517" data-mp="mlp96f9hihde">
                    <button className="w-full bg-white text-[#0A5F8F] hover:bg-blue-50 text-lg py-4 px-6 rounded-xl font-bold transition-colors" onClick={() => document.getElementById('contact')?.scrollIntoView({
                    behavior: 'smooth'
                  })} data-id="element-2518" data-mp="iknca531ihel">
                      לתיאום שיעור
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50" data-id="element-2519" data-mp="cile10e7ja6t">
        <div className="container mx-auto px-4" data-id="element-2520" data-mp="ckobu6llq7m3">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2521" data-mp="hihhmyizszlu">
            <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-600 px-4 py-2 rounded-full font-bold mb-4" data-id="element-2522" data-mp="qjnzbaxdoggo">
              <Sailboat className="w-5 h-5" data-id="element-2523" data-mp="vfd3jrkceljb" />
              <span data-id="element-2524" data-mp="10firoxrelpwn">המקום המושלם</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-2525" data-mp="10dxu0z9slquk">
              למה חוף מעגן מושלם לספורט רוח?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-2526" data-mp="1a6deq4ch4tj5">
              תנאים אידיאליים לווינג פויל וגלישת רוח לכל הרמות
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2527" data-mp="1j6ije6qa9dtm">
            {spotFeatures.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-lg border border-cyan-100" data-id="element-2528" data-mp="33p2rrkp016zy" data-mpi={index}>
                <div className="bg-cyan-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-4" data-id="element-2529" data-mp="2pr4iuutg62ru">
                  <feature.icon className="w-7 h-7 text-cyan-600" data-id="element-2530" data-mp="3mh5088rhlgz4" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2531" data-mp="3iftjksqlc632">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed" data-id="element-2532" data-mp="38o5n2df6fyuq">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-[#0A5F8F] to-[#0D7AB8] text-white" data-id="element-2533" data-mp="57grtnl1obkd">
        <div className="container mx-auto px-4" data-id="element-2534" data-mp="o3fj6ekizkax">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto text-center" data-id="element-2535" data-mp="o4qmyedz0s21">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-2536" data-mp="f9ayydzwd74d">
              מוכנים לעוף מעל המים?
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed" data-id="element-2537" data-mp="5cxxuv20vqzl">
              הצטרפו לקהילת הווינג פויל והגלישה שלנו
              <br data-id="element-2538" data-mp="ad2a28vwdzk0" />
              נלמד אתכם לנצל כל רוח!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-2539" data-mp="3si5lscgdd9te">
              <Button className="bg-white text-[#0A5F8F] hover:bg-blue-50 text-lg py-6 px-8" onClick={() => navigate('/wingfoil')} data-id="element-2540" data-mp="dl7w1fuu3ss">
                למידע על Wing Foil
              </Button>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg py-6 px-8" onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-2541" data-mp="1urvwq9wg8xyk">
                צור קשר
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <ContactSection data-id="element-2542" data-mp="3p0htkgrfkbkc" />
      <Footer data-id="element-2543" data-mp="3p1sywl0ixrdk" />
    </div>;
}